/**
 * Phase 45 Plan 45-05 — AttentionCard.
 *
 * One card in the "Needs attention" rail.
 *
 * Source: UI-SPEC §Surface 1 Needs-attention rail. Severity line auto-derived
 * from burnPct / dueDays / status:
 *   - burnPct > 1 → "{N}% over budget"
 *   - dueDays < 0 → "{N}d overdue"
 *   - else        → "Off track"
 */
import { StatusPill } from './StatusPill';
import { BurnBar } from './BurnBar';
import type { PortfolioRow } from '../../services/api/portfolio';

export interface AttentionCardProps {
  row: PortfolioRow;
}

function severityLine(row: PortfolioRow): string {
  if (row.burnPct > 1) {
    return `${Math.round((row.burnPct - 1) * 100)}% over budget`;
  }
  if (row.dueDays !== null && row.dueDays < 0) {
    return `${Math.abs(row.dueDays)}d overdue`;
  }
  return 'Off track';
}

export function AttentionCard({ row }: AttentionCardProps) {
  const budget = row.hoursBudget ?? 0;
  const hoursLine =
    budget > 0
      ? `${Math.round(row.hoursLogged)}h / ${Math.round(budget)}h`
      : `${Math.round(row.hoursLogged)}h logged`;

  return (
    <div className="bg-ms-surface border border-red-200 rounded-lg p-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-ms-text-muted truncate">
            {row.customerName ?? 'No customer'}
          </div>
          <div className="text-[13px] font-medium text-ms-text-strong truncate">
            {row.fullName ?? row.name}
          </div>
        </div>
        <StatusPill status={row.status} />
      </div>
      <div className="text-[11px] text-red-700 font-medium mb-1.5">
        {severityLine(row)}{' '}
        <span className="text-ms-text-muted font-normal">· {hoursLine}</span>
      </div>
      <BurnBar pct={row.burnPct} />
    </div>
  );
}
